import type { FC } from 'react'
import styled from '@emotion/styled'

import Image from '@segment/matcha/components/Image'
import Paper from '@segment/matcha/components/Paper'

import Typography from '@segment/matcha/components/Typography'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import TestimonialImagePng2x from './assets/TestimonialImage@2x.png'
import TestimonialImageWebp from './assets/TestimonialImage.webp'
import TestimonialImageWebp2x from './assets/TestimonialImage@2x.webp'

const data = {
  description: `“Segment is a reliable customer data platform that helped us scale. Using Segment throughout the years has saved us the time required to connect and sync data between tools. Easiness of use, perfect documentation and the ability to debug events in Segment makes it the perfect tool."`,
  author: `Daniel Korczynski — Head of Product, Enterprise`,
}

const Testimonial: FC = () => {
  return (
    <SectionBase variant="noCopy">
      <Section.Contents>
        <Card {...data} />
      </Section.Contents>
    </SectionBase>
  )
}

const TestimonialImage: FC = () => (
  <picture>
    <source type="image/webp" srcSet={`${TestimonialImageWebp}, ${TestimonialImageWebp2x} 2x`} />
    <Image src={TestimonialImagePng2x} alt="Illustration: Leader Spring 2023" width="351" height="264" loading="lazy" />
  </picture>
)

interface CardProps {
  avatar?: string
  description: string
  author: string
}

const Card: FC<CardProps> = ({ avatar, description, author }) => {
  return (
    <StyledPaper background="transparent" borderRadius={10} elevation={2}>
      <Wrapper>
        <CopySlot>
          <Description tag="p" variant="pMedium">
            {description}
          </Description>
          <AccessoriesRow>
            {avatar && <Author alt={author} src={avatar} />}
            <AuthorName tag="p" variant="textBoldSmall">
              {author}
            </AuthorName>
          </AccessoriesRow>
        </CopySlot>
        <ImageSlot>
          <TestimonialImage />
        </ImageSlot>
      </Wrapper>
    </StyledPaper>
  )
}

const ImageSlot = styled.div`
  grid-area: image;

  padding-top: 16px;
  ${({ theme }) => theme.breakpoints.up(`md`)} {
    padding-top: 0;
    align-content: center;
    display: grid;
  }
`

const StyledPaper = styled(Paper)`
  max-width: 844px;
  width: 100%;
  margin: 0 auto;
  align-items: center;
`

const Wrapper = styled.div`
  display: grid;

  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    grid-template-areas:
      'image'
      'copy';
  }

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    grid-template-columns: 1fr minmax(0, 351px);
    grid-template-areas: 'copy image';
  }
`

const CopySlot = styled.div`
  padding: 16px 32px;
  grid-area: copy;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    padding: 48px;
  }
`

const Description = styled(Typography)`
  color: ${({ theme }) => theme.palette.twilio.gray[80]};
  margin-top: 0;
`

const Author = styled(Image)`
  object-fit: cover;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin: 0;
`

const AuthorName = styled(Typography)`
  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    margin: 16px 0;
  }
`

const AccessoriesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: left;
  gap: 8px;
`

export default Testimonial
